'use client';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations, useLocale } from 'next-intl';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  roleKeys,
  rolesApi,
  getRoleDisplayName,
  type Role
} from '@/features/roles';
import { useAssignRoleToUserMutation } from '../services/users.mutations';
import type { User } from '../types';

const createSchema = (t: (key: string) => string) =>
  z.object({
    role: z.string().min(1, t('roleRequired'))
  });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

interface AssignRoleToUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function AssignRoleToUserDialog({
  open,
  onOpenChange,
  user
}: AssignRoleToUserDialogProps) {
  const t = useTranslations('users');
  const tv = useTranslations('validation');
  const locale = useLocale();

  const schema = useMemo(() => createSchema(tv), [tv]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      role: ''
    }
  });

  const rolesSelect = useInfiniteSelect<Role>({
    queryKey: roleKeys.lists(),
    queryFn: (params) => rolesApi.getAll(params),
    mapOption: useCallback(
      (role: Role) => ({
        value: role.name,
        label: getRoleDisplayName(role, locale),
        color: role.color
      }),
      [locale]
    ),
    enabled: open
  });

  const assignMutation = useAssignRoleToUserMutation();

  async function onSubmit(values: FormValues) {
    if (!user) return;
    try {
      await assignMutation.mutateAsync({
        userId: user.id,
        data: { role: values.role }
      });
      onOpenChange(false);
      form.reset();
    } catch {
      // handled by global MutationCache
    }
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      form.reset();
    }
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{t('assignRole')}</DialogTitle>
          <DialogDescription>
            {t('assignRoleDescription', { name: user?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-6'>
            <FormCombobox
              control={form.control}
              name='role'
              label={t('selectRole')}
              placeholder={t('selectRolePlaceholder')}
              infiniteConfig={rolesSelect}
              required
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={assignMutation.isPending}>
                {assignMutation.isPending && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('assign')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
